import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

interface TelegramConfig {
  telegramBotTokens: {
    manualAlert: string;
    idAlert: string;
    autoAlert: string;
    failedAlert: string;
    depositAlert: string;
  };
  telegramChatId: string;
  telegramSystemBotToken?: string;
  telegramSystemChatId?: string;
  telegramAgentBotToken?: string;
  telegramAgentChatId?: string;
  telegramAgentBotTokens?: {
    manualAlert: string;
    autoAlert: string;
    failedAlert: string;
    depositAlert: string;
  };
  configured: {
    hasManualAlert: boolean;
    hasIdAlert: boolean;
    hasAutoAlert: boolean;
    hasFailedAlert: boolean;
    hasDepositAlert: boolean;
    hasChatId: boolean;
    hasSystemBot?: boolean;
    hasSystemChatId?: boolean;
    hasAgentBot?: boolean;
    hasAgentChatId?: boolean;
    hasAgentManualAlert?: boolean;
    hasAgentAutoAlert?: boolean;
    hasAgentFailedAlert?: boolean;
    hasAgentDepositAlert?: boolean;
  };
}

interface BotTestResult {
  [key: string]: {
    configured: boolean;
    valid: boolean;
    bot?: any;
    error?: string;
  };
}

const TOKEN_KEYS = ['manualAlert', 'idAlert', 'autoAlert', 'failedAlert', 'depositAlert'] as const;
const AGENT_TOKEN_KEYS = ['manualAlert', 'autoAlert', 'failedAlert', 'depositAlert'] as const;

const tokenKeyToAlertType = (key: string) => {
  switch (key) {
    case 'manualAlert':
      return 'MANUAL';
    case 'idAlert':
      return 'ID';
    case 'autoAlert':
      return 'AUTO';
    case 'failedAlert':
      return 'FAILED';
    case 'depositAlert':
      return 'DEPOSIT';
    default:
      return String(key || '').toUpperCase();
  }
};

const agentTokenKeyToTestType = (key: string) => {
  switch (key) {
    case 'manualAlert':
      return 'AGENT_MANUAL';
    case 'autoAlert':
      return 'AGENT_AUTO';
    case 'failedAlert':
      return 'AGENT_FAILED';
    case 'depositAlert':
      return 'AGENT_DEPOSIT';
    default:
      return `AGENT_${String(key || '').toUpperCase()}`;
  }
};

const TelegramNotificationManager: React.FC = () => {
  const [config, setConfig] = useState<TelegramConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showManualAlertModal, setShowManualAlertModal] = useState(false);
  const [showTokenValidation, setShowTokenValidation] = useState(false);
  
  const [showTokens, setShowTokens] = useState<{ [key: string]: boolean }>({});
  const [botTestResults, setBotTestResults] = useState<BotTestResult | null>(null);
  const [configForm, setConfigForm] = useState<{
    telegramBotTokens?: {
      manualAlert?: string;
      idAlert?: string;
      autoAlert?: string;
      failedAlert?: string;
      depositAlert?: string;
    };
    telegramAgentBotTokens?: {
      manualAlert?: string;
      autoAlert?: string;
      failedAlert?: string;
      depositAlert?: string;
    };
    telegramChatId?: string;
    telegramSystemBotToken?: string;
    telegramSystemChatId?: string;
    telegramAgentBotToken?: string;
    telegramAgentChatId?: string;
  }>({});
  const [testForm, setTestForm] = useState({
    alertType: 'MANUAL',
    message: 'Test notification from Data Marketplace'
  });
  const [manualAlertForm, setManualAlertForm] = useState({
    message: '',
    targetAudience: 'admin',
    priority: 'normal'
  });
  const [tokenValidationForm, setTokenValidationForm] = useState({
    botToken: ''
  });

  // Load configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/telegram/config', {
        headers: {
        }
        ,
        credentials: 'include'
      });
      const data = await response.json();
      setConfig(data);
    } catch (error) {

      toast.error('Failed to load Telegram configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const normalizedTokens = Object.entries(configForm.telegramBotTokens || {}).reduce((acc, [key, value]) => {
        const token = String(value || '').trim();
        if (!token || token.startsWith('****')) {
          return acc;
        }
        acc[key] = token;
        return acc;
      }, {} as Record<string, string>);

      const normalizedAgentTokens = Object.entries(configForm.telegramAgentBotTokens || {}).reduce((acc, [key, value]) => {
        const token = String(value || '').trim();
        if (!token || token.startsWith('****')) {
          return acc;
        }
        acc[key] = token;
        return acc;
      }, {} as Record<string, string>);

      const payload: any = {};
      if (Object.keys(normalizedTokens).length > 0) {
        payload.telegramBotTokens = normalizedTokens;
      }

      if (Object.keys(normalizedAgentTokens).length > 0) {
        payload.telegramAgentBotTokens = normalizedAgentTokens;
      }
      if (configForm.telegramChatId !== undefined) {
        payload.telegramChatId = configForm.telegramChatId;
      }

      if (
        configForm.telegramSystemBotToken !== undefined &&
        configForm.telegramSystemBotToken.trim() &&
        !configForm.telegramSystemBotToken.trim().startsWith('****')
      ) {
        payload.telegramSystemBotToken = configForm.telegramSystemBotToken.trim();
      }

      if (configForm.telegramSystemChatId !== undefined) {
        payload.telegramSystemChatId = configForm.telegramSystemChatId;
      }

      if (
        configForm.telegramAgentBotToken !== undefined &&
        configForm.telegramAgentBotToken.trim() &&
        !configForm.telegramAgentBotToken.trim().startsWith('****')
      ) {
        payload.telegramAgentBotToken = configForm.telegramAgentBotToken.trim();
      }

      if (configForm.telegramAgentChatId !== undefined) {
        payload.telegramAgentChatId = configForm.telegramAgentChatId;
      }

      const response = await fetch('/api/admin/telegram/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Telegram configuration updated successfully');
        setShowConfigModal(false);
        loadConfig();
      } else {
        toast.error(data.message || 'Failed to update configuration');
      }
    } catch (error) {

      toast.error('Failed to save configuration');
    }
  };

  const handleTestBots = async () => {
    try {
      setTesting(true);
      const response = await fetch('/api/admin/telegram/test-bots', {
        headers: {
        },
        credentials: 'include'
      });

      const data = await response.json();
      setBotTestResults(data.results);
      
      toast.success(`Bot testing completed: ${data.summary.valid}/${data.summary.configured} bots working`);
    } catch (error) {

      toast.error('Failed to test Telegram bots');
    } finally {
      setTesting(false);
    }
  };

  const handleTestSpecificBot = async () => {
    try {
      setTesting(true);
      const response = await fetch(`/api/admin/telegram/test-bot/${testForm.alertType.toLowerCase()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ message: testForm.message })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Test notification sent successfully via ${testForm.alertType} bot`);
        setShowTestModal(false);
      } else {
        toast.error(data.message || 'Test notification failed');
      }
    } catch (error) {

      toast.error('Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  const handleSendManualAlert = async () => {
    try {
      setTesting(true);
      const response = await fetch('/api/admin/telegram/send-manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(manualAlertForm)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Manual alert sent successfully');
        setShowManualAlertModal(false);
        setManualAlertForm({ message: '', targetAudience: 'admin', priority: 'normal' });
      } else {
        toast.error(data.message || 'Failed to send manual alert');
      }
    } catch (error) {

      toast.error('Failed to send manual alert');
    } finally {
      setTesting(false);
    }
  };

  const handleValidateToken = async () => {
    try {
      setTesting(true);
      const response = await fetch('/api/admin/telegram/validate-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ botToken: tokenValidationForm.botToken })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Bot token is valid! Bot: @${data.bot.username}`);
      } else {
        toast.error(data.message || 'Invalid bot token');
      }
    } catch (error) {

      toast.error('Failed to validate bot token');
    } finally {
      setTesting(false);
    }
  };

  const getBotStatusIcon = (alertType: string) => {
    if (!botTestResults) return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    
    const result = botTestResults[alertType];
    if (!result?.configured) return <XCircleIcon className="h-5 w-5 text-gray-400" />;
    if (result?.valid) return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    return <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getBotStatusBadge = (alertType: string) => {
    if (!botTestResults) return <Badge variant="warning">Unknown</Badge>;
    
    const result = botTestResults[alertType];
    if (!result?.configured) return <Badge variant="secondary">Not Configured</Badge>;
    if (result?.valid) return <Badge variant="success">Working</Badge>;
    return <Badge variant="danger">Failed</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Telegram Notifications
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Configure and manage Telegram bot notifications for system alerts
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Configured Bots</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Object.values(config?.configured || {}).filter(Boolean).length}/5
              </p>
            </div>
            <CogIcon className="h-8 w-8 text-primary-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Working Bots</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {botTestResults ? Object.values(botTestResults).filter(b => b.valid).length : '?'}
              </p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Chat ID</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {config?.telegramChatId ? 'Configured' : 'Not Set'}
              </p>
            </div>
            <BellIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </Card>
      </div>

      {/* Bot Configuration Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {TOKEN_KEYS.map((key) => {
          const value = config?.telegramBotTokens?.[key] || '';
          return (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <BellIcon className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h3>
                </div>
                {getBotStatusIcon(key)}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Status
                  </span>
                  {getBotStatusBadge(key)}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Bot Token
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {value ? '****' + value.slice(-4) : 'Not Set'}
                    </span>
                    <button
                      onClick={() => setShowTokens({
                        ...showTokens,
                        [key]: !showTokens[key]
                      })}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {showTokens[key] ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setTestForm({ ...testForm, alertType: tokenKeyToAlertType(key) });
                    setShowTestModal(true);
                  }}
                  disabled={!config?.configured[`has${key.charAt(0).toUpperCase() + key.slice(1)}` as keyof typeof config.configured]}
                  className="flex-1"
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  Test
                </Button>
              </div>
            </Card>
          </motion.div>
          );
        })}
      </div>

      {/* Dedicated Bot Configuration Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <BellIcon className="h-6 w-6 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Alerts</h3>
              </div>
              {getBotStatusIcon('systemAlert')}
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                {getBotStatusBadge('systemAlert')}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Bot Token</span>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {config?.telegramSystemBotToken ? '****' + String(config.telegramSystemBotToken).slice(-4) : 'Not Set'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Chat ID</span>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {config?.telegramSystemChatId || config?.telegramChatId || 'Not Set'}
                </span>
              </div>
            </div>

            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setTestForm({ ...testForm, alertType: 'SYSTEM' });
                  setShowTestModal(true);
                }}
                disabled={!config?.configured?.hasSystemBot}
                className="flex-1"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                Test
              </Button>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <BellIcon className="h-6 w-6 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Store Alerts</h3>
              </div>
              {getBotStatusIcon('agentAlert')}
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                {getBotStatusBadge('agentAlert')}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Bot Token</span>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {config?.telegramAgentBotToken ? '****' + String(config.telegramAgentBotToken).slice(-4) : 'Not Set'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Chat ID</span>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {config?.telegramAgentChatId || config?.telegramChatId || 'Not Set'}
                </span>
              </div>
            </div>

            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setTestForm({ ...testForm, alertType: 'AGENT' });
                  setShowTestModal(true);
                }}
                disabled={!config?.configured?.hasAgentBot}
                className="flex-1"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                Test
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Agent Store Token Set */}
      <div className="mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <BellIcon className="h-6 w-6 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Store Token Set</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {AGENT_TOKEN_KEYS.map((key) => {
              const maskedToken = config?.telegramAgentBotTokens?.[key] || '';
              const statusKey =
                key === 'manualAlert'
                  ? 'agentManualAlert'
                  : key === 'autoAlert'
                    ? 'agentAutoAlert'
                    : key === 'failedAlert'
                      ? 'agentFailedAlert'
                      : 'agentDepositAlert';

              const configuredFlag =
                key === 'manualAlert'
                  ? config?.configured?.hasAgentManualAlert
                  : key === 'autoAlert'
                    ? config?.configured?.hasAgentAutoAlert
                    : key === 'failedAlert'
                      ? config?.configured?.hasAgentFailedAlert
                      : config?.configured?.hasAgentDepositAlert;

              return (
                <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {`Agent ${key.replace(/([A-Z])/g, ' $1').trim()}`}
                    </div>
                    {getBotStatusIcon(statusKey)}
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {maskedToken ? `****${String(maskedToken).slice(-4)}` : 'Not Set'}
                    </div>
                    {getBotStatusBadge(statusKey)}
                  </div>

                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setTestForm({ ...testForm, alertType: agentTokenKeyToTestType(key) });
                      setShowTestModal(true);
                    }}
                    disabled={!configuredFlag}
                  >
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Test
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-8">
        <Button
          variant="primary"
          onClick={() => {
            setConfigForm({
              telegramBotTokens: config?.telegramBotTokens || {},
              telegramAgentBotTokens: config?.telegramAgentBotTokens || {},
              telegramChatId: config?.telegramChatId,
              telegramSystemBotToken: config?.telegramSystemBotToken || '',
              telegramSystemChatId: config?.telegramSystemChatId,
              telegramAgentBotToken: config?.telegramAgentBotToken || '',
              telegramAgentChatId: config?.telegramAgentChatId
            });
            setShowConfigModal(true);
          }}
        >
          <CogIcon className="h-5 w-5 mr-2" />
          Configure Bots
        </Button>

        <Button
          variant="secondary"
          onClick={handleTestBots}
          disabled={testing}
        >
          {testing ? (
            <LoadingSpinner size="sm" />
          ) : (
            <ArrowPathIcon className="h-5 w-5 mr-2" />
          )}
          Test All Bots
        </Button>

        <Button
          variant="secondary"
          onClick={() => setShowManualAlertModal(true)}
          disabled={!config?.configured.hasManualAlert}
        >
          <PaperAirplaneIcon className="h-5 w-5 mr-2" />
          Send Manual Alert
        </Button>

        <Button
          variant="secondary"
          onClick={() => setShowTokenValidation(true)}
        >
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          Validate Token
        </Button>
      </div>

      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configure Telegram Bots"
        size="large"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telegram Chat ID
            </label>
            <input
              type="text"
              value={configForm.telegramChatId || ''}
              onChange={(e) => setConfigForm({ 
                ...configForm, 
                telegramChatId: e.target.value 
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="@channel_name or chat_id"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TOKEN_KEYS.map((key) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {key.replace(/([A-Z])/g, ' $1').trim()} Bot Token
                </label>
                <input
                  type="password"
                  value={configForm.telegramBotTokens?.[key as keyof typeof configForm.telegramBotTokens] || ''}
                  onChange={(e) => setConfigForm({
                    ...configForm,
                    telegramBotTokens: {
                      ...configForm.telegramBotTokens,
                      [key]: e.target.value as any
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter bot token"
                />
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Dedicated Bots</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  System Notification Bot Token
                </label>
                <input
                  type="password"
                  value={configForm.telegramSystemBotToken || ''}
                  onChange={(e) => setConfigForm({ 
                    ...configForm, 
                    telegramSystemBotToken: e.target.value 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter system bot token"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  System Bot Chat ID
                </label>
                <input
                  type="text"
                  value={configForm.telegramSystemChatId || ''}
                  onChange={(e) => setConfigForm({ 
                    ...configForm, 
                    telegramSystemChatId: e.target.value 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="@channel_name or chat_id"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Agent Alert Bot Token
                </label>
                <input
                  type="password"
                  value={configForm.telegramAgentBotToken || ''}
                  onChange={(e) => setConfigForm({ 
                    ...configForm, 
                    telegramAgentBotToken: e.target.value 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter agent bot token"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Agent Bot Chat ID
                </label>
                <input
                  type="text"
                  value={configForm.telegramAgentChatId || ''}
                  onChange={(e) => setConfigForm({ 
                    ...configForm, 
                    telegramAgentChatId: e.target.value 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="@channel_name or chat_id"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Agent Store Token Set</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {AGENT_TOKEN_KEYS.map((key) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {`Agent ${key.replace(/([A-Z])/g, ' $1').trim()} Bot Token`}
                  </label>
                  <input
                    type="password"
                    value={configForm.telegramAgentBotTokens?.[key as keyof typeof configForm.telegramAgentBotTokens] || ''}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      telegramAgentBotTokens: {
                        ...configForm.telegramAgentBotTokens,
                        [key]: e.target.value as any
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter bot token"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setShowConfigModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveConfig}
            >
              Save Configuration
            </Button>
          </div>
        </div>
      </Modal>

      {/* Test Bot Modal */}
      <Modal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        title={`Test ${testForm.alertType} Bot`}
        size="medium"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Test Message
            </label>
            <textarea
              value={testForm.message}
              onChange={(e) => setTestForm({ ...testForm, message: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              placeholder="Enter test message"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setShowTestModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTestSpecificBot}
              disabled={testing}
            >
              {testing ? (
                <LoadingSpinner size="sm" />
              ) : (
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              )}
              Send Test
            </Button>
          </div>
        </div>
      </Modal>

      {/* Manual Alert Modal */}
      <Modal
        isOpen={showManualAlertModal}
        onClose={() => setShowManualAlertModal(false)}
        title="Send Manual Alert"
        size="medium"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message
            </label>
            <textarea
              value={manualAlertForm.message}
              onChange={(e) => setManualAlertForm({ ...manualAlertForm, message: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              placeholder="Enter alert message"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Audience
              </label>
              <select
                value={manualAlertForm.targetAudience}
                onChange={(e) => setManualAlertForm({ ...manualAlertForm, targetAudience: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="admin">Admin</option>
                <option value="agents">Agents</option>
                <option value="all">All Users</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority
              </label>
              <select
                value={manualAlertForm.priority}
                onChange={(e) => setManualAlertForm({ ...manualAlertForm, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setShowManualAlertModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendManualAlert}
              disabled={testing || !manualAlertForm.message.trim()}
            >
              {testing ? (
                <LoadingSpinner size="sm" />
              ) : (
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              )}
              Send Alert
            </Button>
          </div>
        </div>
      </Modal>

      {/* Token Validation Modal */}
      <Modal
        isOpen={showTokenValidation}
        onClose={() => setShowTokenValidation(false)}
        title="Validate Bot Token"
        size="medium"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bot Token
            </label>
            <input
              type="password"
              value={tokenValidationForm.botToken}
              onChange={(e) => setTokenValidationForm({ ...tokenValidationForm, botToken: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter bot token to validate"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setShowTokenValidation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleValidateToken}
              disabled={testing || !tokenValidationForm.botToken.trim()}
            >
              {testing ? (
                <LoadingSpinner size="sm" />
              ) : (
                <CheckCircleIcon className="h-4 w-4 mr-2" />
              )}
              Validate Token
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TelegramNotificationManager;
